// Employee detail modal — opened from the Medarbetare list. Shows the hire's
// basic info + their onboarding programs with per-program progress.
import React, { useEffect, useState } from 'react';
import { resolveMediaUrl } from '@/lib/mediaUrl';
import { useNavigate } from 'react-router-dom';
import { X, Mail, Briefcase, Calendar, GraduationCap, UserMinus } from 'lucide-react';
import type { Employee } from '@/lib/models/engagement';
import { onboardingApi, type EmployeeOnboardingDetail } from '@/lib/api/onboardingApi';
import { engagementApi } from '@/lib/api/engagementApi';
import { ProgressBar } from '@/components/custom-ui/score-bar';
import { OpenAnswersPanel } from '@/components/modules/OpenAnswersPanel';
import { IslandSlideOver } from '@/components/custom-ui/IslandSlideOver';
import { useLinkLauncher } from '@/contexts/LinkLauncherContext';
import { ChattaMedLinkButton } from '@/components/custom-ui/ChattaMedLinkButton';

const fullName = (e: Employee): string =>
    [e.first_name, e.last_name].filter(Boolean).join(' ') || e.email || 'Okänd användare';

const statusLabel: Record<string, string> = {
    not_started: 'Ej påbörjad',
    in_progress: 'Pågår',
    completed: 'Klar',
};

const EmployeeDetailModal: React.FC<{ employee: Employee; onClose: () => void; onChanged?: () => void }> = ({ employee, onClose, onChanged }) => {
    const navigate = useNavigate();
    const { stageLink } = useLinkLauncher();
    const [details, setDetails] = useState<EmployeeOnboardingDetail[] | null>(null);
    const [offboarding, setOffboarding] = useState(false);

    useEffect(() => {
        let cancelled = false;
        onboardingApi.getEmployeeOnboarding(employee.user_id).then((d) => {
            if (!cancelled) setDetails(d);
        });
        return () => { cancelled = true; };
    }, [employee.user_id]);

    const handleOffboard = async () => {
        if (!window.confirm(`Avsluta anställningen för ${fullName(employee)}? Personen tas bort från Medarbetare.`)) return;
        setOffboarding(true);
        try {
            await engagementApi.endEngagement(employee.engagement_id);
            onChanged?.();
            onClose();
        } catch (e) {
            console.error('[EmployeeDetailModal] offboard failed:', e);
            setOffboarding(false);
        }
    };

    return (
        <IslandSlideOver onClose={onClose} widthClass="max-w-lg">
                {/* Header */}
                <div className="flex items-start justify-between p-6 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                        {employee.picture_url ? (
                            <img src={resolveMediaUrl(employee.picture_url)} alt="" className="w-12 h-12 rounded-full object-cover bg-gray-100" />
                        ) : (
                            <div className="w-12 h-12 rounded-full bg-[#A4C3B2] flex items-center justify-center text-base font-semibold text-[#1A4D4F]">
                                {(employee.first_name?.[0] ?? '') + (employee.last_name?.[0] ?? '') || '?'}
                            </div>
                        )}
                        <div>
                            <h2 className="text-lg font-bold text-gray-900">{fullName(employee)}</h2>
                            <p className="text-sm text-gray-500">Medarbetare</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                    {/* Basics */}
                    <div className="space-y-2 text-sm">
                        {employee.email && (
                            <div className="flex items-center gap-2 text-gray-600">
                                <Mail className="w-4 h-4 text-gray-400" />
                                <a href={`mailto:${employee.email}`} className="text-[#1A4D4F] hover:underline">{employee.email}</a>
                            </div>
                        )}
                        {employee.unit_name && (
                            <div className="flex items-center gap-2 text-gray-600">
                                <Briefcase className="w-4 h-4 text-gray-400" /> {employee.unit_name}
                            </div>
                        )}
                        <div className="flex items-center gap-2 text-gray-600">
                            <Calendar className="w-4 h-4 text-gray-400" />
                            Anställd sedan {new Date(employee.started_at).toLocaleDateString('sv-SE')}
                        </div>
                    </div>

                    {/* Onboarding */}
                    <div>
                        <h3 className="text-sm font-semibold text-gray-900 mb-3">Onboarding</h3>
                        {details === null ? (
                            <p className="text-sm text-gray-400">Laddar…</p>
                        ) : details.length === 0 ? (
                            <div className="text-center py-6 bg-gray-50 rounded-xl">
                                <GraduationCap className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                                <p className="text-sm text-gray-500">Ingen onboarding tilldelad ännu.</p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {details.map((d) => (
                                    <div key={d.program_id} className="border border-gray-100 rounded-xl p-4">
                                        <div className="flex items-center justify-between mb-2">
                                            <p className="font-medium text-gray-900">{d.program_name || 'Program'}</p>
                                            <span className="text-xs text-gray-500">{statusLabel[d.status] ?? d.status}</span>
                                        </div>
                                        <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                                            <span>{d.completed}/{d.total} moduler</span>
                                            <span className="font-semibold text-[#1A4D4F]">{d.percentage}%</span>
                                        </div>
                                        <ProgressBar value={d.percentage} color={d.percentage >= 100 ? 'green' : 'teal'} />
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* #293 — free-text answers this employee has written in the
                        modules they have worked through. Self-hiding when there are
                        none, so it costs nothing on courses that ask no open
                        questions. */}
                    <OpenAnswersPanel
                        source={{ kind: 'employee', userId: employee.user_id }}
                        title="Fritextsvar"
                    />
                </div>

                {/* Footer — manage onboarding + offboard */}
                <div className="flex justify-between items-center p-6 border-t border-gray-100 flex-shrink-0">
                    <div className="flex items-center gap-1">
                        <button
                            onClick={() => { onClose(); navigate('/dashboard/onboarding'); }}
                            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-[#1A4D4F] hover:bg-gray-100 rounded-xl transition-colors"
                        >
                            <GraduationCap className="w-4 h-4" />
                            Hantera onboarding
                        </button>
                        <ChattaMedLinkButton
                            onClick={() => {
                                // #301 — staged (unsent) @-tagged prompt referencing this
                                // medarbetare, ready for the user to review + send.
                                onClose();
                                stageLink({
                                    reference: { type: 'employee', id: employee.user_id },
                                    label: fullName(employee),
                                    prompt: 'Hjälp mig med den här medarbetaren.',
                                });
                            }}
                        />
                    </div>
                    <button
                        onClick={handleOffboard}
                        disabled={offboarding}
                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-xl disabled:opacity-50 transition-colors"
                    >
                        <UserMinus className="w-4 h-4" />
                        {offboarding ? 'Avslutar...' : 'Avsluta anställning'}
                    </button>
                </div>
        </IslandSlideOver>
    );
};

export default EmployeeDetailModal;
